/* eslint-disable linebreak-style */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/jsx-no-constructed-context-values */
import React from 'react';
import axios, { AxiosInstance } from 'axios';
import { createUseStyles } from 'react-jss';
import { v4 as uuid } from 'uuid';
import { MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import { isObject } from 'underscore';
import { useMsal } from '@azure/msal-react';
import { useToken } from './TokenProvider';
import { useLogger } from '../Logger';
import { useNotificationApi } from '../NotificationApi';
import { PlaygroundTheme } from '../../Theme';

type AxiosContextValue = {
  idTokenInstance?: AxiosInstance,
  accessTokenInstance?: AxiosInstance,
};

const AxiosContext = React.createContext<AxiosContextValue>({});

const useStyles = createUseStyles<string, unknown, PlaygroundTheme>((theme) => ({
  spinnerOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000001, // zIndex of Fluent UI popover is 1000000

    '& .ms-Spinner-circle': {
      borderColor: `${theme.themePrimary} ${theme.themeLighterAlt} ${theme.themeLighterAlt}`,
    },
  },
}));

export const AxiosInstaceProvider: React.FunctionComponent<React.PropsWithChildren> = (props) => {
  const logger = useLogger();
  const classes = useStyles();
  const [accessTokenInstance, setAccessTokenInstance] = React.useState<AxiosInstance>();
  const [idTokenInstance, setIdTokenInstance] = React.useState<AxiosInstance>();
  const [pendingRequestCount, setPendingRequestCount] = React.useState<number>(0);
  const {
    idToken,
    accessToken,
    refreshInteractively,
  } = useToken();
  const { accounts } = useMsal();
  const { showNotification } = useNotificationApi();

  const onTokenExpiry = React.useCallback(() => {
    refreshInteractively(false);
  }, [refreshInteractively]);

  React.useEffect(() => {
    if (idToken) {
      const instance = axios.create({
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      instance.interceptors.request.use(
        (config) => {
          setPendingRequestCount((prev) => prev + 1);
          const requestId = uuid();

          // eslint-disable-next-line no-param-reassign
          config.params = {
            ...config.params,
          };

          // https://github.com/dotnet/runtime/blob/main/src/libraries/System.Diagnostics.DiagnosticSource/src/HttpCorrelationProtocol.md
          // Azure Application Insights use Request-Id header to correlate requests
          config.headers.set('Request-Id', requestId);
          logger.logInfo(`${config.method?.toUpperCase()} ${config.url} starts`, {
            Request_Id: requestId,
          });

          return config;
        },
      );

      instance.interceptors.response.use(
        (response) => {
          setPendingRequestCount((prev) => prev - 1);
          const requestId = response.config.headers['Request-Id'];
          logger.logInfo(`${response.config.method?.toUpperCase()} ${response.config.url} ends`, {
            Request_Id: requestId,
          });

          return response;
        },
        (error) => {
          setPendingRequestCount((prev) => prev - 1);
          const requestId = error.config.headers['Request-Id'];

          if (error.response) {
            if (error.response.status === 400) {
              showNotification('Bad request', MessageBarType.warning);
            } else if (error.response.status === 401) {
              logger.logWarning('Token is rejected by backend servers. Trying to refresh', {
                Request_Id: requestId,
              });
              // Now that ID Tokens are refreshed more aggressively, we should hardly run into this
              // In case it happens, show a notification as warning
              showNotification('Your session just expired. Please try again...', MessageBarType.warning);

              onTokenExpiry();
            } else if (error.response.status === 403) {
              showNotification(
                `You are not allowed to access the application. Contact support with the ID for additional help: ${accounts[0].localAccountId}`,
                MessageBarType.error,
                30000, // 30 seconds. Extra display time for user to note down the ID
              );
            } else {
              if (error.response.status >= 500) {
                showNotification('Internal Error. Please try again later. If the issue persists, please contact developers.', MessageBarType.error);
              }
              logger.logError(`Status: ${error.response.status}. Data: ${isObject(error.response.data) ? JSON.stringify(error.response.data) : error.response.data})}`, {
                Request_Id: requestId,
              });
            }
          } else {
            logger.logError(`Error message: ${error.message}`, {
              Request_Id: requestId,
            });
          }

          return error;
        },
      );

      // HACK: If we use setXXX(instance), React take instance as a factory function
      setIdTokenInstance(() => instance);
    }
  }, [idToken, onTokenExpiry, logger, showNotification]);

  React.useEffect(() => {
    if (accessToken) {
      const instance = axios.create({
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      instance.interceptors.request.use(
        (config) => {
          setPendingRequestCount((prev) => prev + 1);

          return config;
        },
      );

      instance.interceptors.response.use(
        (response) => {
          setPendingRequestCount((prev) => prev - 1);

          return response;
        },
        (error) => {
          setPendingRequestCount((prev) => prev - 1);

          if (error.response) {
            if (error.response.status === 401) {
              logger.logError('Token is rejected. Trying to refresh');
              onTokenExpiry();
            } else {
              logger.logError(`Status: ${error.response.status}. \n Data: ${error.response.data}`);
            }
          } else {
            logger.logError(`Error message: ${error.message}`);
          }

          return error;
        },
      );

      // HACK: Same as above
      setAccessTokenInstance(() => instance);
    }
  }, [accessToken, onTokenExpiry, logger]);

  return (
    <AxiosContext.Provider value={{
      idTokenInstance,
      accessTokenInstance,
    }}
    >
      {props.children}
      {
            pendingRequestCount > 0 && (
            <div className={classes.spinnerOverlay}>
              <Spinner size={SpinnerSize.large} />
            </div>
            )
        }
    </AxiosContext.Provider>
  );
};

export const useAxios = () => React.useContext<AxiosContextValue>(AxiosContext);
